import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, Check, Leaf, Shield, Award, 
  Package, Clock, ChevronRight, Sparkles,
  Heart, ShoppingBag
} from 'lucide-react';
import { 
  getProductBySlug, 
  getProductsByCategory, 
  getCategoryById,
  wellnessCategories 
} from '../../data/wellnessData';

const WellnessDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProductBySlug(slug);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Product Not Found</h1>
          <Link to="/products/wellness">
            <Button className="bg-[#263776]">Back to Wellness Catalogue</Button>
          </Link>
        </div>
      </div>
    );
  }

  const category = getCategoryById(product.category);
  const relatedProducts = getProductsByCategory(product.category)
    .filter(p => p.id !== product.id)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-orange-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/products" className="text-gray-500 hover:text-[#263776]">Products</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <Link to="/products/wellness" className="text-gray-500 hover:text-[#263776]">Wellness</Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">{category?.name}</span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-[#263776] font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Main Product Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/products/wellness')}
            className="mb-6 text-[#263776] hover:text-[#F47D20]"
            data-testid="back-to-wellness-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Wellness
          </Button>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="relative">
              <div className="sticky top-24">
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full aspect-square object-cover"
                      data-testid="product-image"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <Shield className="w-24 h-24 text-gray-300" />
                    </div>
                  )}
                </div>
                
                {/* Trust Badges */}
                <div className="flex justify-center gap-4 mt-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Leaf className="w-5 h-5 text-green-600" />
                    <span>100% Natural</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <span>Lab Tested</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Award className="w-5 h-5 text-amber-600" />
                    <span>GMP Certified</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div>
              {/* Category & Brand */}
              <div className="flex items-center gap-3 mb-4">
                <Badge 
                  className="text-white"
                  style={{ backgroundColor: category?.color || '#263776' }}
                >
                  {category?.name}
                </Badge>
                <Badge variant="outline" className="border-[#F47D20] text-[#F47D20]">
                  <Sparkles className="w-3 h-3 mr-1" /> ORIVANA
                </Badge>
              </div>

              {/* Product Name & Tagline */}
              <h1 className="text-3xl md:text-4xl font-bold text-[#263776] mb-2" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-xl text-[#F47D20] font-medium mb-6">
                {product.tagline}
              </p>

              {/* Description */}
              <p className="text-gray-600 mb-8 leading-relaxed">
                {product.description}
              </p>

              {/* Benefits */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-[#263776] mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#F47D20]" />
                  Key Benefits
                </h3>
                <div className="grid gap-3">
                  {product.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-green-50 rounded-lg p-3">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specifications */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-[#263776] mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#F47D20]" />
                  Product Specifications
                </h3>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="border-b border-gray-200 pb-2 last:border-0">
                        <div className="text-sm text-gray-500">{key}</div>
                        <div className="font-medium text-gray-800">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Usage */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-[#263776] mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#F47D20]" />
                  How to Use
                </h3>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <p className="text-gray-700">{product.usage}</p>
                </div>
              </div>

              {/* Ingredients */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-[#263776] mb-4 flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-[#F47D20]" />
                  Ingredients
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.ingredients.map((ingredient, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className="bg-white border-gray-200 text-gray-700 py-1.5 px-3"
                    >
                      {ingredient}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link to="/seller-application">
                  <Button 
                    size="lg" 
                    className="bg-[#F47D20] hover:bg-[#e06910]"
                    data-testid="become-supplier-btn"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Become a Supplier
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white"
                    data-testid="request-quote-btn"
                  >
                    Request Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-[#263776]">
                More in {category?.name}
              </h2>
              <Link to="/products/wellness">
                <Button variant="ghost" className="text-[#F47D20]">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link 
                  key={relatedProduct.id}
                  to={`/products/wellness/${relatedProduct.slug}`}
                  data-testid={`related-${relatedProduct.slug}`}
                >
                  <Card className="group h-full overflow-hidden hover:shadow-xl transition-all duration-300">
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      {relatedProduct.image ? (
                        <img 
                          src={relatedProduct.image}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Shield className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-[#263776] group-hover:text-[#F47D20] transition-colors mb-1">
                        {relatedProduct.name}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {relatedProduct.tagline}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Categories Quick Links */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-lg font-semibold text-[#263776] mb-6 text-center">
            Explore Other Health Concerns
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {wellnessCategories
              .filter(c => c.id !== product.category)
              .map((cat) => (
                <Link
                  key={cat.id}
                  to={`/products/wellness?category=${cat.id}`}
                  onClick={() => {
                    navigate('/products/wellness');
                  }}
                >
                  <Badge 
                    variant="outline"
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    {cat.name}
                  </Badge>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WellnessDetail;
